import { useState, useEffect, useRef } from 'react';
import { useNavigationType } from 'react-router-dom';

export function usePersistedListState(listId, defaultPageSize = 10) {
    const navType = useNavigationType();

    const hasClearedRef = useRef(false);
    if (!hasClearedRef.current) {
        if (navType !== 'POP') {
            const keysToClear = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith(`${listId}_`)) {
                    keysToClear.push(key);
                }
            }
            keysToClear.forEach(key => sessionStorage.removeItem(key));
        }
        hasClearedRef.current = true;
    }

    const getInitialState = (key, defaultValue) => {
        const saved = sessionStorage.getItem(`${listId}_${key}`);
        if (saved !== null) {
            try { return JSON.parse(saved); } catch (e) { return saved; }
        }
        return defaultValue;
    };

    const [currentPage, setCurrentPage] = useState(() => getInitialState('currentPage', 0));
    const [pageSize, setPageSize] = useState(() => getInitialState('pageSize', defaultPageSize));
    const [searchTerm, setSearchTerm] = useState(() => getInitialState('searchTerm', ''));
    const [userSelected, setUserSelected] = useState(() => getInitialState('userSelected', ''));
    const [statusSelected, setStatusSelected] = useState(() => getInitialState('statusSelected', ''));
    const [dateDebutValue, setDateDebutValue] = useState(() => getInitialState('dateDebutValue', ''));
    const [dateFinValue, setDateFinValue] = useState(() => getInitialState('dateFinValue', ''));
    const [selectedProduct, setSelectedProduct] = useState(() => getInitialState('selectedProduct', null));

    useEffect(() => {
        sessionStorage.setItem(`${listId}_currentPage`, JSON.stringify(currentPage));
        sessionStorage.setItem(`${listId}_pageSize`, JSON.stringify(pageSize));
        sessionStorage.setItem(`${listId}_searchTerm`, JSON.stringify(searchTerm));
        sessionStorage.setItem(`${listId}_userSelected`, JSON.stringify(userSelected));
        sessionStorage.setItem(`${listId}_statusSelected`, JSON.stringify(statusSelected));
        sessionStorage.setItem(`${listId}_dateDebutValue`, JSON.stringify(dateDebutValue));
        sessionStorage.setItem(`${listId}_dateFinValue`, JSON.stringify(dateFinValue));
        sessionStorage.setItem(`${listId}_selectedProduct`, JSON.stringify(selectedProduct));
    }, [currentPage, pageSize, searchTerm, userSelected, statusSelected, dateDebutValue, dateFinValue, selectedProduct, listId]);

    /**
     * Saves the current scroll position of the given container element.
     * If no element is provided, falls back to window.scrollY.
     * @param {HTMLElement|null} containerEl - The scrollable container DOM element.
     */
    const saveScrollPosition = (containerEl = null) => {
        const scrollValue = containerEl ? containerEl.scrollTop : window.scrollY;
        sessionStorage.setItem(`${listId}_scroll`, scrollValue.toString());
    };

    return {
        currentPage, setCurrentPage,
        pageSize, setPageSize,
        searchTerm, setSearchTerm,
        userSelected, setUserSelected,
        statusSelected, setStatusSelected,
        dateDebutValue, setDateDebutValue,
        dateFinValue, setDateFinValue,
        selectedProduct, setSelectedProduct,
        saveScrollPosition
    };
}

/**
 * Restores the saved scroll position after the data finishes loading.
 * @param {string} listId - The unique list identifier.
 * @param {boolean} loading - Whether data is still loading.
 * @param {React.RefObject} [containerRef] - Optional ref to the scrollable container.
 *   If provided, restores scrollTop on that element instead of the window.
 */
export function useScrollRestore(listId, loading, containerRef = null) {
    useEffect(() => {
        if (!loading) {
            const savedScroll = sessionStorage.getItem(`${listId}_scroll`);
            if (savedScroll) {
                // Remove it so it doesn't trigger on every tab switch
                sessionStorage.removeItem(`${listId}_scroll`);
                const scrollValue = parseInt(savedScroll, 10);
                setTimeout(() => {
                    if (containerRef && containerRef.current) {
                        containerRef.current.scrollTop = scrollValue;
                    } else {
                        window.scrollTo(0, scrollValue);
                    }
                }, 150);
            }
        }
    }, [loading, listId, containerRef]);
}
